import { SiKick, SiTwitch, SiYoutube } from "@icons-pack/react-simple-icons";
import { ReactNode } from "react";

import { StreamProvider } from "@/gen/graphql";

export const getProviderDisplayName = (provider: StreamProvider): string => {
    switch (provider) {
        case "YOUTUBE":
            return "YouTube";
        case "TWITCH":
            return "Twitch";
        case "KICK":
            return "Kick";
        default:
            return provider;
    }
};

export const getProviderIcon = (provider: StreamProvider) => {
    switch (provider) {
        case "YOUTUBE":
            return SiYoutube;
        case "TWITCH":
            return SiTwitch;
        case "KICK":
            return SiKick;
        default:
            return () => null;
    }
};

export const getHandlePlaceholder = (provider: StreamProvider): string => {
    switch (provider) {
        case "YOUTUBE":
            return "@channelname or channel-name";
        case "TWITCH":
            return "username";
        case "KICK":
            return "username";
        default:
            return "Channel handle";
    }
};

export const getPlatformIdPlaceholder = (provider: StreamProvider): string => {
    switch (provider) {
        case "YOUTUBE":
            return "UCxxxxxxxxxxxxxxxxxxxxx";
        case "TWITCH":
            return "123456789 (numeric channel ID)";
        case "KICK":
            return "username";
        default:
            return "Platform-specific ID";
    }
};

export const getHandleHelpText = (provider: StreamProvider): string => {
    switch (provider) {
        case "YOUTUBE":
            return "The channel name as it appears in URLs (with or without @)";
        case "TWITCH":
            return "The Twitch username (4-25 characters, letters, numbers, underscore)";
        case "KICK":
            return "The Kick username (3-25 characters)";
        default:
            return "";
    }
};

export const getPlatformIdHelpText = (provider: StreamProvider): ReactNode => {
    switch (provider) {
        case "YOUTUBE":
            return (
                <>
                    You can use this tool to find it:{" "}
                    <a
                        href="https://www.streamweasels.com/tools/youtube-channel-id-and-user-id-convertor/"
                        target="_blank"
                    >
                        https://www.streamweasels.com/tools/youtube-channel-id-and-user-id-convertor/
                    </a>
                </>
            );
        case "TWITCH":
            return (
                <>
                    You can use this tool to find it:{" "}
                    <a
                        href="https://www.streamweasels.com/tools/convert-twitch-username-to-user-id/"
                        target="_blank"
                    >
                        https://www.streamweasels.com/tools/convert-twitch-username-to-user-id/
                    </a>
                </>
            );
        case "KICK":
            return "Same as username for Kick";
        default:
            return "";
    }
};

export const validateExternalChannelHandle = (
    handle: string,
    provider: StreamProvider
): boolean => {
    switch (provider) {
        case "YOUTUBE":
            // YouTube handles can be @username, channel names, or channel IDs
            return /^@?[a-zA-Z0-9_.-]{1,100}$/.test(handle.replace(/^@/, ""));
        case "TWITCH":
            // Twitch usernames: 4-25 characters, alphanumeric and underscores
            return /^[a-zA-Z0-9_]{4,25}$/.test(handle);
        case "KICK":
            // Kick usernames: similar to Twitch but allow dots and dashes
            return /^[a-zA-Z0-9_.-]{3,25}$/.test(handle);
        default:
            return true;
    }
};

export const validateExternalChannelId = (
    externalChannelId: string,
    provider: StreamProvider
): boolean => {
    switch (provider) {
        case "YOUTUBE":
            // YouTube channel IDs start with UC and are 24 characters
            return /^UC[a-zA-Z0-9_-]{22}$/.test(externalChannelId);
        case "TWITCH":
            // Twitch channel IDs are numeric
            return /^\d+$/.test(externalChannelId);
        case "KICK":
            return /^[a-zA-Z0-9_.-]{3,25}$/.test(externalChannelId);
        default:
            return true;
    }
};
